import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from "../components/StandardButton";

const DEFAULT_IP = "192.168.1.0";
const DEFAULT_PREFIX = 24;

const ipToInt = (ip) => {
  const parts = ip.split(".");
  if (parts.length !== 4) return null;
  let n = 0;
  for (let i = 0; i < 4; i += 1) {
    if (!/^\d+$/.test(parts[i])) return null;
    const o = Number(parts[i]);
    if (o < 0 || o > 255) return null;
    n = (n * 256) + o;
  }
  return n;
};

const intToIp = (n) => {
  return [
    (n >>> 24) & 0xff,
    (n >>> 16) & 0xff,
    (n >>> 8) & 0xff,
    n & 0xff,
  ].join(".");
};

const intToBinaryDottedQuad = (n) => {
  return [
    ((n >>> 24) & 0xff).toString(2).padStart(8, "0"),
    ((n >>> 16) & 0xff).toString(2).padStart(8, "0"),
    ((n >>> 8) & 0xff).toString(2).padStart(8, "0"),
    (n & 0xff).toString(2).padStart(8, "0"),
  ].join(".");
};

const prefixToMask = (prefix) => {
  if (prefix === 0) return 0;
  return (0xffffffff << (32 - prefix)) >>> 0;
};

// Convert a dotted-quad mask integer to a prefix length. Returns null if the
// mask is non-contiguous (e.g. 255.255.255.1), which is not a valid IPv4 subnet mask.
const maskToPrefix = (n) => {
  let count = 0;
  let seenZero = false;
  for (let i = 31; i >= 0; i -= 1) {
    const bit = (n >>> i) & 1;
    if (bit === 1) {
      if (seenZero) return null;
      count += 1;
    } else {
      seenZero = true;
    }
  }
  return count;
};

const ipClass = (firstOctet) => {
  if (firstOctet < 128) return "A";
  if (firstOctet < 192) return "B";
  if (firstOctet < 224) return "C";
  if (firstOctet < 240) return "D";
  return "E";
};

const classifyAddress = (n) => {
  const a = (n >>> 24) & 0xff;
  const b = (n >>> 16) & 0xff;
  const c = (n >>> 8) & 0xff;
  if (n === 0xffffffff) return "broadcast";
  if (a === 0) return "this_network";
  if (a === 127) return "loopback";
  if (a === 169 && b === 254) return "link_local";
  if (a === 100 && b >= 64 && b <= 127) return "cgnat";
  if (a === 10) return "private";
  if (a === 172 && b >= 16 && b <= 31) return "private";
  if (a === 192 && b === 168) return "private";
  if (a === 192 && b === 0 && c === 2) return "documentation";
  if (a === 198 && b === 51 && c === 100) return "documentation";
  if (a === 203 && b === 0 && c === 113) return "documentation";
  if (a === 198 && (b === 18 || b === 19)) return "benchmarking";
  if (a >= 224 && a <= 239) return "multicast";
  if (a >= 240) return "reserved";
  return "public";
};

const parseInput = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return { error: "cidr_calc_error_empty" };

  // Accepted forms:
  //   192.168.1.0                       → defaults to /24
  //   192.168.1.0/24                    → CIDR prefix
  //   192.168.1.0/255.255.255.0         → dotted-quad netmask after slash
  //   192.168.1.0 255.255.255.0         → space-separated address + netmask
  let ipPart;
  let suffix = null;
  if (trimmed.includes("/")) {
    const segs = trimmed.split("/");
    if (segs.length !== 2) return { error: "cidr_calc_error_invalid" };
    ipPart = segs[0].trim();
    suffix = segs[1].trim();
  } else if (/\s/.test(trimmed)) {
    const segs = trimmed.split(/\s+/);
    if (segs.length !== 2) return { error: "cidr_calc_error_invalid" };
    ipPart = segs[0];
    suffix = segs[1];
  } else {
    ipPart = trimmed;
  }

  const ip = ipToInt(ipPart);
  if (ip === null) return { error: "cidr_calc_error_invalid_ip" };

  let prefix = DEFAULT_PREFIX;
  if (suffix !== null && suffix !== "") {
    if (/^\d+$/.test(suffix)) {
      prefix = Number(suffix);
      if (prefix < 0 || prefix > 32) return { error: "cidr_calc_error_invalid_prefix" };
    } else if (suffix.includes(".")) {
      const maskInt = ipToInt(suffix);
      if (maskInt === null) return { error: "cidr_calc_error_invalid_mask" };
      const derived = maskToPrefix(maskInt);
      if (derived === null) return { error: "cidr_calc_error_invalid_mask" };
      prefix = derived;
    } else {
      return { error: "cidr_calc_error_invalid_prefix" };
    }
  }
  return { ip, prefix };
};

const computeSubnet = (ip, prefix) => {
  const mask = prefixToMask(prefix);
  const wildcard = (~mask) >>> 0;
  const network = (ip & mask) >>> 0;
  const broadcast = (network | wildcard) >>> 0;
  const total = prefix === 0 ? 4294967296 : Math.pow(2, 32 - prefix);
  let usable;
  let firstHost;
  let lastHost;
  if (prefix >= 31) {
    usable = prefix === 32 ? 1 : 2;
    firstHost = network;
    lastHost = broadcast;
  } else {
    usable = total - 2;
    firstHost = network + 1;
    lastHost = broadcast - 1;
  }
  return {
    mask,
    wildcard,
    network,
    broadcast,
    total,
    usable,
    firstHost,
    lastHost,
  };
};

const CidrCalculator = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState(`${DEFAULT_IP}/${DEFAULT_PREFIX}`);

  const result = useMemo(() => {
    const parsed = parseInput(input);
    if (parsed.error) return { error: parsed.error };
    const subnet = computeSubnet(parsed.ip, parsed.prefix);
    return { ip: parsed.ip, prefix: parsed.prefix, ...subnet };
  }, [input]);

  const handlePrefixChange = (newPrefix) => {
    const parsed = parseInput(input);
    if (parsed.error) {
      setInput(`${DEFAULT_IP}/${newPrefix}`);
      return;
    }
    setInput(`${intToIp(parsed.ip)}/${newPrefix}`);
  };

  const copyValue = async (text) => {
    if (!text) {
      toast.error(t("nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const reset = () => setInput(`${DEFAULT_IP}/${DEFAULT_PREFIX}`);

  const rows = !result.error
    ? [
        {
          labelKey: "cidr_calc_result_network",
          value: `${intToIp(result.network)}/${result.prefix}`,
        },
        {
          labelKey: "cidr_calc_result_netmask",
          value: intToIp(result.mask),
        },
        {
          labelKey: "cidr_calc_result_wildcard",
          value: intToIp(result.wildcard),
        },
        {
          labelKey: "cidr_calc_result_broadcast",
          value: result.prefix >= 31 ? "—" : intToIp(result.broadcast),
        },
        {
          labelKey: "cidr_calc_result_first_host",
          value: result.usable > 0 ? intToIp(result.firstHost) : "—",
        },
        {
          labelKey: "cidr_calc_result_last_host",
          value: result.usable > 0 ? intToIp(result.lastHost) : "—",
        },
        {
          labelKey: "cidr_calc_result_total",
          value: result.total.toLocaleString(),
        },
        {
          labelKey: "cidr_calc_result_usable",
          value: result.usable.toLocaleString(),
        },
        {
          labelKey: "cidr_calc_result_class",
          value: ipClass((result.ip >>> 24) & 0xff),
        },
        {
          labelKey: "cidr_calc_result_visibility",
          value: t(`cidr_calc_visibility_${classifyAddress(result.ip)}`),
        },
        {
          labelKey: "cidr_calc_result_binary_mask",
          value: intToBinaryDottedQuad(result.mask),
          mono: true,
        },
        {
          labelKey: "cidr_calc_result_binary_network",
          value: intToBinaryDottedQuad(result.network),
          mono: true,
        },
      ]
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t("cidr_calc_title")}
              </h1>
              <p className="text-gray-600 mt-1">{t("cidr_calc_intro")}</p>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                {t("cidr_calc_input_label")}
              </label>
              <input
                type="text"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono text-sm"
                placeholder={t("cidr_calc_input_placeholder")}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                spellCheck={false}
              />
              <p className="text-xs text-gray-500">
                {t("cidr_calc_input_help")}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                {t("cidr_calc_prefix_label")}
                {!result.error && (
                  <span className="ml-2 text-indigo-700 font-mono">
                    /{result.prefix}
                  </span>
                )}
              </label>
              <input
                type="range"
                min={0}
                max={32}
                step={1}
                value={result.error ? DEFAULT_PREFIX : result.prefix}
                onChange={(e) => handlePrefixChange(Number(e.target.value))}
                className="w-full"
                aria-label={t("cidr_calc_prefix_label")}
              />
              <div className="flex flex-wrap gap-2">
                {[8, 16, 24, 25, 26, 27, 28, 30, 32].map((p) => (
                  <StandardButton
                    key={p}
                    onClick={() => handlePrefixChange(p)}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    /{p}
                  </StandardButton>
                ))}
                <StandardButton
                  onClick={reset}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t("cidr_calc_reset")}
                </StandardButton>
              </div>
            </div>

            {result.error && (
              <p className="text-sm text-red-600">{t(result.error)}</p>
            )}

            {!result.error && (
              <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {rows.map((row) => (
                    <div
                      key={row.labelKey}
                      className="flex items-center justify-between gap-3 bg-white border border-indigo-100 rounded px-3 py-2"
                    >
                      <div className="flex flex-col min-w-0">
                        <span className="text-xs uppercase tracking-wide text-gray-500">
                          {t(row.labelKey)}
                        </span>
                        <span
                          className={`${row.mono ? "font-mono" : ""} text-gray-800 break-all text-sm`}
                        >
                          {row.value}
                        </span>
                      </div>
                      <StandardButton
                        onClick={() => copyValue(String(row.value))}
                        type="secondary"
                        variant="outlined"
                        size="small"
                      >
                        {t("copy_button")}
                      </StandardButton>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("cidr_calc_note_edge")}</p>
              <p>{t("cidr_calc_note_private")}</p>
              <p>{t("cidr_calc_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default CidrCalculator;
